import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { HtkoConfigService } from './htko-config.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-htko-config',
    templateUrl: './htko-config.component.html',
    styleUrls: ['./htko-config.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class HtkoConfigComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  isEditMode = false;
  loadingData = true;
  modalTitle = "Add Config";

  // Global service data
  globalService: any;
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  workspaceFactory: any;

  // Component data
  rowData: any;
  gridData: any;
  colData: any;
  filterParams: any;
  baseURLs: any = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  /**
   * Modal callback name read from `WorkspaceService.activeScreen.data` (set by
   * `CeHostDialogComponent.bridgeDataToActiveScreen()`). Drives the
   * client-side variant differentiation: when it equals
   * `'genBranchForm1118ConfigNew'`, the component renders the General/Branch
   * basket dropdown (sourced from `q5wgil` instead of `dbty56`) and rewrites
   * the percentage row labels from "High Taxed Kick Out - …" to
   * "Amount to be Resourced - …". This restores the exact behaviour the
   * legacy `gtw-ui-workspace` bundle (still embedded as a custom element in
   * the old AngularJS host) used to provide via its own `@Input('modal-name')`.
   */
  modalName: string = '';

  /**
   * True when the active toolbar action is `genBranchForm1118ConfigNew`.
   * Toggles: (1) basket dropdown source — `q5wgil` instead of `dbty56`, and
   * (2) percentage row label rewrites in `getConfigLineLabel()`.
   */
  showGeneralBranchContext: boolean = false;

  /**
   * Basket rows for the General/Branch variant (action_code `q5wgil`). Same
   * shape as `dbty56` (CODE_KEY, CODE_DESCRIPTION).
   */
  generalBranchBasketData: any[] = [];
  selectedGeneralBranchBasket: string = '';
  originalSelectedGeneralBranchBasket: string = '';

  // Form data
  filterTaxYear: string = '';
  scenario: string = '';
  jcd_key: string = '';
  sourceData: any[] = [];
  selectedSourceCode: any = null;
  filing_group_key: string = '';
  filer_entity_key: string = '';
  savedData: any[] = [];
  originalSavedData: any[] = [];
  originalSelectedSourceCode: any = null;
  htkoConfigCodeMastData: any[] = [];
  gotExistingData: any[] = [];
  operationType: string = 'I';
  is_locked: any = null;
  logged_in_user: string = '';
  object_id: any = null;


  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private htkoConfigService: HtkoConfigService
  ) {
  }

  ngOnInit(): void {
    console.log(`${this.constructor.name} initialized`);
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gp = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp);
    this.globalService = gp;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.workspaceFactory = this.gtwWorkspace.workspaceFactory;
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = fp;
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : (row || {});
    this.gridData = gridPayloadFromActiveScreen(activeScreen);

    // Modal callback name. Bridged from
    // `CeHostDialogComponent.bridgeDataToActiveScreen()` into
    // `activeScreen.data` (or via `colData.call_back` for grid actions). The
    // toolbar action is the sole discriminator between the two variants —
    // see `showGeneralBranchContext`, `getConfigLineLabel()` and the
    // `q5wgil` basket call below.
    const asData: any = (activeScreen as any)?.data || {};
    this.colData = asData.colData || {};
    this.modalName = String(
      asData.modal_name ||
        asData.modalName ||
        this.colData?.call_back ||
        ''
    ).trim();
    this.showGeneralBranchContext =
      this.modalName === 'genBranchForm1118ConfigNew';
  }

  init(): void {
    try {
      // Following old AngularJS pattern: Only need filterParams and GENERAL_CONFIG
      // rowData and gridData are optional (used for edit mode and pre-selecting basket)
      if (this.filterParams && Object.keys(this.filterParams).length > 0 &&
          this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
        
        // Set basic data - matching old code pattern (lines 31-39 in old controller)
        // filterTaxYear comes from filterParams (old: filterParams.tax_year)
        this.filterTaxYear = this.filterParams.tax_year || '';
        // scenario comes from globalService (old: GlobalService.globalParams.scenario)
        this.scenario = this.filterParams.scenario ||this.globalService?.scenario || '';
        // jcd_key comes from globalService (old: GlobalService.globalParams.jcd_key)
        this.jcd_key = this.globalService?.jcd_key || '';
        // filing_group_key comes from filterParams (old: filterParams.filing_group_key)
        this.filing_group_key = this.filterParams.filing_group_key || '';
        // filer_entity_key comes from filterParams (old: filterParams.filer_entity_key)
        this.filer_entity_key = this.filterParams.filer_entity_key || '';
        
        // Get logged in user (old: line 21 - vm.userSettings.user.sso_id)
        if (this.USER_SETTINGS?.user?.sso_id) {
          this.logged_in_user = this.USER_SETTINGS.user.sso_id;
        }

        // Check lock status (old: lines 63-78 - called immediately)
        this.checkLockStatus();

        // Standard basket dropdown comes from `dbty56`; the General/Branch
        // variant uses a separate code-mast call (`q5wgil`) instead — same
        // shape, different population (legacy gtw-ui-workspace behaviour).
        if (!this.showGeneralBranchContext) {
          this.getCodeMastData();
        } else {
          this.sourceData = [];
          this.selectedSourceCode = '';
          this.getGenBranchBasketData();
        }

        // Get saved config data (old: line 124 - called immediately)
        this.getSavedConfigData();
        
        // Set saved values based on edit/create mode (old: line 189 - called immediately)
        // This checks gridData, not rowData - gridData determines edit vs add mode
        this.setSavedValues();
      }
    } catch (e) {
      console.error('Error in HtkoConfig init:', e);
    }
  }

  checkLockStatus(): void {
    this.htkoConfigService.checkLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcd_key,
      this.globalService,
      this.filterParams,
      this.modalName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data.jsonObject;
        if (this.is_locked != undefined && this.is_locked != null && 
            this.is_locked[0]?.LOCK_STATUS == 'Y') {
          // this.cancel();
          this.gtwSnackbarService.openSnackBar(
            `Tax Return has already been filed for tax year ${this.filterTaxYear}.`,
            'gtw-snackbar--red'
          );
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  gethtkoConfigCodeMast(): void {
    this.htkoConfigService.getHtkoConfigCodeMast(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.modalName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.htkoConfigCodeMastData = _.filter(data.jsonObject, (p: any) => {
          return _.includes(['FORM_1118_HTKO_CONFIG'], p.CODE_GROUP);
        });
        // After getting code mast data, set saved values if in add mode
        if ((!this.gridData || Object.keys(this.gridData).length === 0 || 
             (this.gridData && !this.gridData.rowData))) {
          this.setSavedValuesForAdd();
        }
      },
      (error: any) => {
        console.error('Error getting HTKO config code mast:', error);
      }
    );
  }

  setSavedValuesForAdd(): void {
    if (this.htkoConfigCodeMastData && this.htkoConfigCodeMastData.length > 0) {
      this.savedData = [];
      this.htkoConfigCodeMastData.forEach((item: any) => {
        const tempObj: any = {};
        tempObj["ADJ_TYPE_KEY"] = item.CODE_KEY;
        tempObj["CODE_DESC"] = item.CODE_DISP_DESC;
        tempObj["CODE_SHORT_DESC"] = item.CODE_DISP_DESC;
        tempObj["CONFIG_PERCENTAGE"] = null;
        tempObj["ADJ_KEY"] = null;
        this.savedData.push(tempObj);
      });
      // Store original values for reset functionality in add mode
      this.storeOriginalValues();
      this.loadingData = false;
    }
  }

  getCodeMastData(): void {
    this.htkoConfigService.getCodeMastData(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcd_key,
      this.globalService,
      this.filterParams,
      this.modalName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
        } else {
          this.sourceData = _.filter(data.jsonObject, (item: any) => {
            return (item.CODE_GROUP === 'SOURCE_CODE' && 
                    item.CODE_DESCRIPTION != 'Passive' && 
                    item.CODE_DESCRIPTION != 'Exempt');
          });
          
          // In edit mode, use gridData.rowData.BASKET_KEY; otherwise use rowData.BASKET_KEY
          if (this.isEditMode && this.gridData && this.gridData.rowData && this.gridData.rowData.BASKET_KEY) {
            this.selectedSourceCode = this.gridData.rowData.BASKET_KEY;
          } else if (this.rowData && this.rowData.BASKET_KEY) {
            this.selectedSourceCode = this.rowData.BASKET_KEY;
          }
        }
      },
      (error: any) => {
        console.error('Error getting code mast data:', error);
        this.gtwSnackbarService.openSnackBar('Error loading basket data', 'gtw-snackbar--red');
      }
    );
  }

  /**
   * Basket population for `genBranchForm1118ConfigNew` (action_code `q5wgil`).
   * Same UI binding as the standard basket; different backend source — pre-
   * filtered to General/Branch baskets server-side. Falls back to raw data
   * when the SOURCE_CODE filter empties the list (matches legacy fallback).
   */
  getGenBranchBasketData(): void {
    this.htkoConfigService.getGenBranchBasketCodeMast(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcd_key,
      this.globalService,
      this.filterParams,
      this.modalName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          this.generalBranchBasketData = [];
        } else {
          const raw = data.jsonObject || [];
          const filtered = _.filter(raw, (item: any) => {
            return (item.CODE_GROUP === 'SOURCE_CODE' &&
                    item.CODE_DESCRIPTION != 'Passive' &&
                    item.CODE_DESCRIPTION != 'Exempt');
          });
          this.generalBranchBasketData = filtered.length > 0 ? filtered : raw;

          if (this.isEditMode && this.gridData && this.gridData.rowData && this.gridData.rowData.BASKET_KEY) {
            this.onGeneralBranchBasketChange(this.gridData.rowData.BASKET_KEY);
          } else if (this.rowData && this.rowData.BASKET_KEY) {
            this.onGeneralBranchBasketChange(this.rowData.BASKET_KEY);
          }
        }
      },
      (error: any) => {
        console.error('Error getting gen-branch basket data (q5wgil):', error);
        this.gtwSnackbarService.openSnackBar('Error loading basket data', 'gtw-snackbar--red');
        this.generalBranchBasketData = [];
      }
    );
  }

  getSavedConfigData(): void {
    this.htkoConfigService.getSavedConfigData(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.globalService,
      this.filterParams,
      this.modalName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.gotExistingData = data.jsonObject || [];
        // After getting saved data, set saved values if in edit mode
        if (this.gridData && Object.keys(this.gridData).length > 0 && 
            this.gridData.rowData != undefined) {
          this.setSavedValuesForEdit();
        }
      },
      (error: any) => {
        console.error('Error getting saved config data:', error);
      }
    );
  }

  setSavedValuesForEdit(): void {
    // Following old code pattern (lines 144-149): Uses rowData.BASKET_KEY if available
    // rowData comes from gridData.rowData in edit mode
    if (this.gotExistingData && this.gotExistingData.length > 0) {
      this.savedData = [];
      // In edit mode, basket key comes from gridData.rowData (not this.rowData)
      const basketKey = (this.gridData && this.gridData.rowData) ? 
                        this.gridData.rowData.BASKET_KEY : 
                        this.rowData?.BASKET_KEY;
      
      // Pre-select the row's basket. For the gen-branch variant the binding
      // target is `selectedGeneralBranchBasket`, so route through the
      // dedicated change handler instead of clobbering `selectedSourceCode`.
      if (!this.showGeneralBranchContext &&
          this.gridData && this.gridData.rowData && this.gridData.rowData.BASKET_KEY) {
        this.selectedSourceCode = this.gridData.rowData.BASKET_KEY;
      }

      if (basketKey) {
        this.gotExistingData.forEach((exist: any) => {
          if (exist.BASKET_KEY == basketKey) {
            // Create a deep copy to avoid reference issues
            this.savedData.push(_.cloneDeep(exist));
          }
        });
      }

      if (this.showGeneralBranchContext && basketKey) {
        this.onGeneralBranchBasketChange(basketKey);
      }

      // Store original values for reset functionality
      this.storeOriginalValues();
      this.loadingData = false;
    }
  }
  
  storeOriginalValues(): void {
    // Store original savedData and selected basket(s) for reset functionality
    this.originalSavedData = _.cloneDeep(this.savedData);
    this.originalSelectedSourceCode = this.selectedSourceCode;
    this.originalSelectedGeneralBranchBasket = this.selectedGeneralBranchBasket;
  }

  setSavedValues(): void {
    // When click on edit (pencil icon) from Grid Data Screen
    if (this.gridData != null && Object.keys(this.gridData).length > 0) {
      const tempGridData = _.clone(this.gridData);
      
      if (tempGridData.rowData != undefined) {
        // Set the update params
        this.operationType = 'U';
        this.isEditMode = true;
        this.modalTitle = "Edit Config";
        
        // Get the saved data for this basket
        // This will be called from getSavedConfigData callback
        if (this.gotExistingData && this.gotExistingData.length > 0) {
          this.setSavedValuesForEdit();
        }
      } else {
        // Set the insert params
        this.operationType = 'I';
        this.isEditMode = false;
        this.modalTitle = "Add Config";
        
        // Get the config code mast
        // This will call setSavedValuesForAdd when data is loaded
        this.gethtkoConfigCodeMast();
      }
    } else {
      // Add mode - no grid data
      this.operationType = 'I';
      this.isEditMode = false;
      this.modalTitle = "Add Config";
      
      // Get the config code mast
      // This will call setSavedValuesForAdd when data is loaded
      this.gethtkoConfigCodeMast();
    }
  }

  save(): void {
    // To Block user from saving for Return filed year
    if (this.is_locked != undefined && this.is_locked != null && 
        this.is_locked[0]?.LOCK_STATUS == 'Y') {
      this.cancel();
      this.gtwSnackbarService.openSnackBar(
        "You cannot perform this action as Tax return is already filed.",
        'gtw-snackbar--red'
      );
      return;
    }

    // 🚀 CRITICAL: Validate all required fields before saving
    if (this.showGeneralBranchContext &&
        (this.selectedGeneralBranchBasket === undefined ||
         this.selectedGeneralBranchBasket === null ||
         this.selectedGeneralBranchBasket === '')) {
      this.gtwSnackbarService.openSnackBar('Please select Basket.', 'gtw-snackbar--red');
      this.isSaveClicked = false;
      return;
    }

    if (!this.showGeneralBranchContext &&
        (this.selectedSourceCode === undefined || this.selectedSourceCode === null ||
         this.selectedSourceCode === '')) {
      this.gtwSnackbarService.openSnackBar('Please select Basket.', 'gtw-snackbar--red');
      this.isSaveClicked = false;
      return;
    }

    if (!this.filing_group_key || this.filing_group_key === '') {
      this.gtwSnackbarService.openSnackBar("Please fill all fields.", 'gtw-snackbar--red');
      this.isSaveClicked = false;
      return;
    }

    if (!this.filer_entity_key || this.filer_entity_key === '') {
      this.gtwSnackbarService.openSnackBar("Please fill all fields.", 'gtw-snackbar--red');
      this.isSaveClicked = false;
      return;
    }

    if (!this.savedData || this.savedData.length === 0) {
      this.gtwSnackbarService.openSnackBar("Please fill all fields.", 'gtw-snackbar--red');
      this.isSaveClicked = false;
      return;
    }

    // Check if at least one item has a valid CONFIG_PERCENTAGE
    let hasValidPercentage = false;
    for (let indx in this.savedData) {
      if (this.savedData[indx].CONFIG_PERCENTAGE !== null && 
          this.savedData[indx].CONFIG_PERCENTAGE !== '' && 
          this.savedData[indx].CONFIG_PERCENTAGE !== undefined) {
        hasValidPercentage = true;
        break;
      }
    }

    if (!hasValidPercentage) {
      this.gtwSnackbarService.openSnackBar("Please fill all fields.", 'gtw-snackbar--red');
      this.isSaveClicked = false;
      return;
    }

    // Disable the Save button till the processing completes
    this.isSaveClicked = true;
    this.crudLoading = true;

    const dataToBeSaved: any[] = [];
    const effectiveBasketKey = this.getEffectiveBasketKey();

    for (let indx in this.savedData) {
      if (this.savedData[indx].CONFIG_PERCENTAGE !== 0 &&
          (this.savedData[indx].CONFIG_PERCENTAGE == null || 
           this.savedData[indx].CONFIG_PERCENTAGE == '')) {
        this.gtwSnackbarService.openSnackBar("Please enter a valid Amount/Percentage.", 'gtw-snackbar--red');
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }

      // Check for duplicate in insert mode
      if (this.operationType == 'I' && this.gotExistingData != undefined && 
          this.gotExistingData != null && this.gotExistingData.length > 0) {
        let checkExist = false;
        this.gotExistingData.forEach((exist: any) => {
          if (exist.BASKET_KEY == effectiveBasketKey &&
              exist.CONSOL_GROUP_KEY == this.filing_group_key && 
              exist.FILER_LE_KEY == this.filer_entity_key) {
            checkExist = true;
          }
        });

        if (checkExist) {
          this.gtwSnackbarService.openSnackBar(
            "High Taxed Kick Out Configuration for the selected Tax Year and Filer Entity is already configured.",
            'gtw-snackbar--red'
          );
          this.isSaveClicked = false;
          this.crudLoading = false;
          return;
        }
      }

      const returnObj: any = {};
      returnObj['ADJ_KEY'] = this.savedData[indx].ADJ_KEY;
      returnObj['ADJ_TYPE_KEY'] = this.savedData[indx].ADJ_TYPE_KEY;
      returnObj['CONFIG_PERCENTAGE'] = this.savedData[indx].CONFIG_PERCENTAGE;
      returnObj["OPERATION_TYPE"] = this.operationType;
      returnObj["BASKET_KEY"] = effectiveBasketKey;
      returnObj['FILING_GROUP_KEY'] = this.filing_group_key;
      returnObj['FILER_ENTITY_KEY'] = this.filer_entity_key;
      
      dataToBeSaved.push(returnObj);
    }

    const message = "High Taxed Kick Out Configuration has been successfully saved/updated";

    const returnClobSettingsObj: any = {};
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['tax_year'] = this.filterTaxYear;
    returnClobSettingsObj['scenario'] = this.scenario;
    returnClobSettingsObj['screen_key'] = this.globalService?.screen_key;
    returnClobSettingsObj['jcd_key'] = this.jcd_key;
    returnClobSettingsObj['object_id'] = this.object_id;
    returnClobSettingsObj['filer_entity_key'] = this.filer_entity_key;

    // Get filer entity dropdown to get combination_key
    this.getFilerEntityDropdown(returnClobSettingsObj, dataToBeSaved, message);
  }

  getFilerEntityDropdown(returnClobSettingsObj: any, dataToBeSaved: any[], message: string): void {
    if (this.filing_group_key != '') {
      this.htkoConfigService.getFilerEntityDropdown(
        this.GENERAL_CONFIG.base_url,
        this.filterTaxYear,
        this.scenario,
        this.filing_group_key,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          const filerEntityArr = data.jsonObject;
          const filerEntityDropdown: any[] = Object.assign([], 
            _.filter(filerEntityArr, { FILER_LE_KEY: this.filer_entity_key }));
          
          if (filerEntityDropdown && filerEntityDropdown.length > 0) {
            const selectedFECombinationKey = filerEntityDropdown[0].FILER_COMBINATION_KEY;
            returnClobSettingsObj['combination_key'] = selectedFECombinationKey;
            
            // Send to save the Data after getting combination key details
            this.sendDetails(returnClobSettingsObj, dataToBeSaved, message);
          }
        },
        (error: any) => {
          console.error('Error getting filer entity dropdown:', error);
          this.isSaveClicked = false;
          this.crudLoading = false;
        }
      );
    }
  }

  sendDetails(returnClobSettingsObj: any, dataToBeSaved: any[], message: string): void {
    this.htkoConfigService.saveData(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      dataToBeSaved,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;

        // Handle response like AngularJS: result.data.errorMessage
        const response = result.data || result;
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // Emit refresh string for AngularJS integration
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  reset(): void {
    if (this.operationType == 'I') {
      // Add mode: Clear all form fields
      // 🔴 CRITICAL: Force dropdown reset by setting to null first, then empty string
      // This ensures Angular detects the change even on subsequent reset calls
      // Pattern: Always set to null first (different from empty string) to force change detection
      // This matches the pattern used in tax-liability-dashboard component
      if (!this.showGeneralBranchContext) {
        this.selectedSourceCode = null;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.selectedSourceCode = '';
          this.onBasketChange('');
          this.cdr.detectChanges();
        }, 0);
      }

      if (this.showGeneralBranchContext) {
        this.onGeneralBranchBasketChange('');
      }

      // Clear all percentage fields
      this.savedData.forEach((item: any) => {
        item.CONFIG_PERCENTAGE = null;
      });
    } else {
      // Edit mode: Restore original values that were stored when form was first loaded
      if (this.originalSavedData && this.originalSavedData.length > 0) {
        // Restore savedData from originalSavedData (deep copy to avoid reference issues)
        this.savedData = _.cloneDeep(this.originalSavedData);
        // Restore selected basket(s) to original value
        this.selectedSourceCode = this.originalSelectedSourceCode;
        if (this.showGeneralBranchContext) {
          this.selectedGeneralBranchBasket = this.originalSelectedGeneralBranchBasket;
        }
        this.loadingData = false;
        this.cdr.detectChanges();
      } else {
        // Fallback: If originalSavedData is not available, restore from gridData
        if (this.showGeneralBranchContext) {
          if (this.gridData && this.gridData.rowData && this.gridData.rowData.BASKET_KEY) {
            this.onGeneralBranchBasketChange(this.gridData.rowData.BASKET_KEY);
          } else if (this.rowData && this.rowData.BASKET_KEY) {
            this.onGeneralBranchBasketChange(this.rowData.BASKET_KEY);
          }
        } else if (this.gridData && this.gridData.rowData && this.gridData.rowData.BASKET_KEY) {
          this.selectedSourceCode = this.gridData.rowData.BASKET_KEY;
        } else if (this.rowData && this.rowData.BASKET_KEY) {
          this.selectedSourceCode = this.rowData.BASKET_KEY;
        }

        // Try to restore from gotExistingData if available
        if (this.gotExistingData && this.gotExistingData.length > 0) {
          this.savedData = [];
          const basketKey = (this.gridData && this.gridData.rowData) ? 
                            this.gridData.rowData.BASKET_KEY : 
                            this.rowData?.BASKET_KEY;
          
          if (basketKey) {
            this.gotExistingData.forEach((exist: any) => {
              if (exist.BASKET_KEY == basketKey) {
                this.savedData.push(_.cloneDeep(exist));
              }
            });
          }
          this.loadingData = false;
          this.cdr.detectChanges();
        } else {
          // Last resort: Reload from server
          this.savedData = [];
          if (!this.showGeneralBranchContext) {
            this.getCodeMastData();
          } else {
            this.getGenBranchBasketData();
          }
          this.getSavedConfigData();
        }
      }
    }
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  onKeyPress(event: any): boolean {
    if (event.target.value && event.target.value.length >= 20) {
      return false;
    }
    return true;
  }

  /**
   * Handle Basket dropdown change
   * Explicitly sets selectedSourceCode when dropdown value changes
   */
  onBasketChange(value: any): void {
    // 🔴 CRITICAL: Explicitly set selectedSourceCode with the selected value
    // Convert to string for consistency (CODE_KEY might be number or string)
    // Empty string for placeholder, otherwise convert to string
    this.selectedSourceCode = (value === null || value === undefined || value === '') ? '' : String(value);
    this.cdr.detectChanges();
  }

  /**
   * Gen-branch basket (`q5wgil`): same string-coercion contract as
   * `onBasketChange()` so the dropdown's `[(ngModel)]` stays consistent with
   * the data we send to the backend.
   */
  onGeneralBranchBasketChange(value: any): void {
    this.selectedGeneralBranchBasket =
      (value === null || value === undefined || value === '') ? '' : String(value);
    this.cdr.detectChanges();
  }

  /**
   * Display-only label rewrite for the percentage row — for
   * `genBranchForm1118ConfigNew` we relabel "High Taxed Kick Out - …" rows as
   * "Amount to be Resourced - …". Backend payload (CODE_KEY, ADJ_TYPE_KEY)
   * is identical; only the visible text changes. This is the legacy
   * gtw-ui-workspace behaviour, ported back so the migrated screen renders
   * the same labels users saw before consolidation.
   */
  getConfigLineLabel(row: any): string {
    const raw = row && row.CODE_DESC != null && row.CODE_DESC !== ''
      ? String(row.CODE_DESC)
      : (row && row.CODE_SHORT_DESC != null && row.CODE_SHORT_DESC !== ''
        ? String(row.CODE_SHORT_DESC)
        : '');
    if (!this.showGeneralBranchContext || !raw) {
      return raw;
    }
    return raw.replace(/High Taxed Kick Out/gi, 'Amount to be Resourced');
  }

  /**
   * Basket key sent to the save payload: gen-branch dropdown selection in
   * the General/Branch variant, otherwise the standard SOURCE_CODE selection.
   */
  private getEffectiveBasketKey(): string {
    if (this.showGeneralBranchContext) {
      return this.selectedGeneralBranchBasket != null && this.selectedGeneralBranchBasket !== ''
        ? String(this.selectedGeneralBranchBasket)
        : '';
    }
    return this.selectedSourceCode != null && this.selectedSourceCode !== ''
      ? String(this.selectedSourceCode)
      : '';
  }
}

